import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import droidSans from 'three/examples/fonts/droid/droid_sans_regular.typeface.json';
import { planets } from './Planets';
import { CATEGORIES, categoryFor } from '../../Utils/artSource';
import { getActiveCamera } from './Initial3DSetup';
import { getFocusedPlanet } from '../../Utils/ringGroup';
import { LOGO_LAYER } from './CornerLogo';
// The second chrome element the scene imports rather than reaches for by id, after `LobbyContainer`'s
// one. The Legend sits where the column needs to be — see `updateNavBar` — and it can only be told
// which list to show, never asked which one it is showing.
import { setLegend } from '../../../Components/Legend';

/**
 * The gallery's six categories, as buttons across the top of the 3D frame, beside the coin.
 *
 * There is no bar behind the row — nothing spanning the six of them. That is what was asked for
 * ("no background, just the buttons") and it is also what the frame can carry: the row is a HUD
 * over a dark lobby and a solar system, and a bar would be a fourth surface competing with the
 * planets for the same black. Each button is its own small plate, hung on nothing.
 *
 * **Why they are meshes and not DOM.** The 3D page is a React app and a row of `<button>`s would
 * have been a tenth of this file. It was not done because the row is placed from the live camera
 * every frame and shares the coin's plane, in the pass `Animate` draws over the world — so it
 * holds its corner through the planet flights, the lobby walk and every resize, using one piece
 * of arithmetic instead of a second, CSS-side copy of the same framing that would drift from the
 * first.
 *
 * **The narrow frame.** The six of them do not fit across a frame that is much taller than it is
 * wide. Every plate is cut from the coin's radius and the coin is a share of the frame's *height*,
 * so the row holds its size while the width it is drawn in walks out from under it — and a portrait
 * phone is only the worst case of a window dragged narrow. What that frame gets is not a smaller row
 * but the other shape of one: the plates stack beneath the head word, a column where there was a bar,
 * and the word comes out with them to be the column's own head — it is what names the six things
 * under it. See `ACROSS_FIT` for where the two forms meet.
 *
 * **The layer.** Buttons are on layer 7, the same device the planet labels (5) and the coin (6)
 * use, and here it earns its keep twice over: every raycaster in the project is pinned to layer 0
 * — `raycast`, `raycastFromCentre`, `raycastImages` — so a button can never swallow a planet
 * click, and `raycastFromCentre` in particular is handed `scene.children` and walks the subtree,
 * so without the layer a button would be a target of the lobby's own reticle. The other half of
 * that trade is `camera.layers.enable(7)` in Initial3DSetup: the camera's mask is what the
 * renderer draws.
 *
 * The pointer is the one raycast that has to reach them, so it is this file's own, masked to the
 * two HUD layers. That is why nothing here shares `Utils/GlobalFunctions`' helpers — they are all
 * layer-0 by construction.
 *
 * **What a button does.** It calls back into MainScene, which is where `selectPlanet` lives and
 * where the one difference between this control and a planet click is implemented: a planet click
 * can only ever start from home, because the canvas sends every press to the artwork once
 * something is focused, so from one ring there is no way to another but by going home first. The
 * row can pick a planet from anywhere, and the camera goes straight across. See `switchPlanet`.
 */

/**
 * Layer for the buttons. See the note above — 5 is the planet labels, 6 is the coin.
 */
const LAYER = 7;

/**
 * The letters are built at unit size once and scaled per frame, rather than rebuilt at the size
 * the viewport wants.
 *
 * `TextGeometry` bakes its `size` into the vertices, so text that has to hold its share of the
 * frame as the window is resized would otherwise mean a new geometry per resize event for every
 * button. Built at 1 and scaled, the geometry is a constant and the frame does six
 * `scale.setScalar` calls — the same trade the coin makes, and the same reason.
 */
const FONT_SIZE = 1;

/**
 * How far the word at the row's head is cut back into the frame, as a share of its own height.
 *
 * This is the head word's cut and no longer the buttons': a letter in a tray is a relief and
 * takes `WORD_DEPTH`. This one stands on nothing, so it keeps the cut the letters wore before
 * there were plates. 0.12 was that depth and came to under two pixels, so the word read as flat
 * type; what says "solid" on a material that takes no light is the band of a second colour on a
 * letter's side, and at the row's size on screen that band wants about five pixels.
 */
const DEPTH_RATIO = 0.42;
const CURVE_SEGMENTS = 3;
const BEVEL = 0.02;

/**
 * The button, as a plate with a tray cut into it and the word lying in the tray. Letter heights.
 *
 * The plate hugs the word it holds across: a fixed margin on either side of the word's own
 * bounding box, so "VR" gets a narrow plaque and "Logo Design" a wide one. `PAD_X` has to clear
 * `RIM_W`, or the rim would close over the letters; it does, which leaves the tray floor a little
 * air of its own around the word inside the frame.
 *
 * Its *height* is not the word's, though it was: every plate in the row is now the same height,
 * taken from the tallest word, because six plates of six heights read as six sizes of button
 * rather than as one bar of them. So `PAD_Y` is not a margin every word keeps — it is the one the
 * tallest word keeps above and below it, and the shorter words keep more. That is why the row's
 * plates line up top and bottom while the words inside them do not quite, and the leftover
 * raggedness is 3.8px at the near end: each word is still centred on its own ink, so a word with a
 * descender sits a little higher than one without. Aligning them on a shared cap line is the next
 * step if that ever shows.
 *
 * The two margins are not the same, and the difference is what the plate is for. On the tallest
 * word — "Logo Design", whose `g` is the only descender in the row — the plate's height comes to
 * about 1.9 times its ink, and it is the air above and below the letters, not the border at their
 * sides, that makes a plate look like something you press. Both margins have been raised once
 * already, and it is this ratio and not the absolute size that the raise was judged on.
 *
 * `RIM_H` is how far the rim's top stands above the tray floor, and `WORD_DEPTH` is how tall the
 * word is where it lies on that floor — so the word's face ends up `RIM_H - WORD_DEPTH - WORD_LIFT`
 * below the rim's top, which is the whole of the recess. `WORD_LIFT` is a hair of clearance, there
 * so the word's back is not coplanar with the floor it rests on.
 */
const PAD_X = 0.4;
const PAD_Y = 0.62;
const RIM_W = 0.2;
const RIM_H = 0.22;
const WORD_DEPTH = 0.12;
const WORD_LIFT = 0.01;

/**
 * A press, in the numbers it is made of.
 *
 * `navTweaks.setback` is a share of the row's distance from the camera rather than of the button,
 * and that is the point: the row sits four units out, so a set-back measured in button heights
 * would be a fraction of a pixel, while eight per cent of the depth is a step you can see — on
 * screen the whole button going back and shrinking by about that much.
 *
 * It is spent along the button's own normal, which is the row's -Z turned by that button's share of
 * the bend. That is the bar's angle answered in the animation: a press pushes into the bar, the way
 * a key goes into the board it is set in, and because the bar is curved the six of them go back
 * along six slightly different directions — each into its own bit of the arc, which is what the
 * curve means. On a flat bar all six are the same direction and this reduces to the one the row
 * used to use.
 *
 * The word rides the button in this, and that is a trade knowingly made rather than a detail.
 * Pinned in place — which is how this started, the plate sinking while its word held still — the
 * word would be walked out of its own tray, because set-back spent along the bar is not set-back
 * spent along the line from the eye. Measured at 1920x1080 on the flat row, that difference is 26px
 * on the plate beside the coin, on a plate 170px wide; and on the shortest word it is 22px on a
 * plate 46px wide, near enough half of it. Along the eye's line the walk is zero to two decimal
 * places and a fixed word would hold, but then each button recedes in its own direction and the row
 * comes apart under the press instead of moving as a bar. One of the two had to give; the bar
 * moving as a bar is the one that carries the angle.
 *
 * It replaced a squash of the button's own scale, and it is worth saying why the squash went.
 * A scale is a 2D move on a 3D object: it pulls the plate in around its own centre rather than
 * sending it away from the eye, so the button would read as smaller rather than as further — and
 * with the word pinned it also pulled the plate's edges in towards letters that were not moving at
 * all, far enough on the longest labels to cross them. The word riding the button removes the
 * second fault; the first is why the move is a distance and not a scale.
 *
 * The button is held down for as long as the pointer is — `mousedown` sinks it, `mouseup` brings
 * it back — and `PRESS_HALF_MS` is how quickly it covers half the distance it has left in either
 * direction, so the two ends of the gesture are one motion rather than an animation and its
 * reverse. Half a distance rather than a duration, because the pointer can come up at any moment:
 * a tap of forty milliseconds and a long lean both have to look like presses, and only the second
 * one has time to be a slow one.
 */
const PRESS_HALF_MS = 30;

/**
 * How the row arrives: the six plates are not all there at once, they come out one after another
 * from the coin's end towards the far one, each growing from nothing and rising out of the arc.
 *
 * `REVEAL_HALF_MS` is the same halfway-to-the-target easing as the press and the head word, and the
 * slowest of the three — this is the one motion that is meant to be watched rather than caught up
 * with. `REVEAL_STAGGER` is the share of that sweep spent merely *starting* the six, one after
 * another; each plate then has the rest of the sweep to complete in, so the last to start is also
 * the last to finish and the row reads as a wipe along the arc rather than as six things blinking on
 * together. `REVEAL_RISE` is how far back along its own normal a plate begins, as a share of the
 * row's distance from the eye, so a plate comes forward out of the curve as it grows.
 *
 * The stagger is by index, which is left to right, because the buttons are added in `CATEGORIES`
 * order — and the direction is the arc's own, so a row bent the other way would still reveal left to
 * right on screen. Shutting the row runs the whole thing backwards, which is the same easing with
 * the target at nought and needs nothing of its own.
 */
const REVEAL_HALF_MS = 70;
const REVEAL_STAGGER = 0.5;
const REVEAL_RISE = 0.3;

/**
 * The head word's own two numbers: how long it takes to come out from behind the coin, and how far
 * back it starts while it is there.
 *
 * The same halfway-to-the-target easing as the press, and a longer half-life for a longer trip: the
 * word travels about a hundred and fifty pixels, and the press's thirty milliseconds would move it
 * most of that in two frames — a slide read as a jump. At ninety the bulk of it is over in about a
 * third of a second, which is a thing the eye can follow and still not wait for.
 *
 * `HINT_SETBACK` is a share of the row's depth, like the press's `setback` and for the same reason —
 * and it is spent along the row's own normal, which at rest is straight back from the camera. The
 * word does not need the ray treatment the press needed: the coin only ever covers its leading end,
 * and moving the whole word straight back slides it very slightly towards the coin rather than away
 * from it, which is the direction it is going anyway.
 *
 * It is here to settle a depth question rather than to be seen. The word is cut deeper than the
 * buttons' — a `TextGeometry`'s default at 0.42 against the tray's 0.12 — so its front face reaches
 * a shade further forward than the coin's does, and left at the row's plane it would win that
 * comparison and draw over the mark instead of behind it. One per cent of the depth puts its face
 * about two hundredths of a unit behind the coin's, which is enough at any angle the coin turns to.
 */
const HINT_HALF_MS = 90;
const HINT_SETBACK = 0.01;

/**
 * How much of the frame's width the six plates may fill before the row stops being a row.
 *
 * The row is a world length and the frame is as wide as its aspect makes it, so the two do not
 * scale together: every plate is cut from the coin's radius, and that radius is `CornerLogo`'s
 * `SIZE` of the frame's *height*. Turn the frame into a portrait one and the row keeps its width
 * while the width it is drawn in walks out from under it. A phone is where that is worst, and it is
 * what the change was asked for, but a window dragged narrow is the same fault — so what is measured
 * here is the fault rather than the device: the row's own half-length against the frame's half-width,
 * both in the plane the row is laid out on.
 *
 * Left short of one so the plates keep a gutter at either end, and read off the camera rather than
 * from a pixel breakpoint for the reason the rest of this file is geometry: a breakpoint here would
 * be a second opinion about the frame's width, written in a different place, and the two would
 * disagree the first time anything about the view changed.
 */
const ACROSS_FIT = 0.9;

/**
 * Everything about the row that is a matter of taste rather than of structure, in one object.
 *
 * These are the production values: the row ships reading exactly these numbers, and they are all
 * one edit away from being different. They are gathered here rather than sprinkled as constants
 * because a dev build puts a panel over the page that writes to this object — see `DevGui.js` —
 * and a panel can only drive what it can reach. Nothing in the shipped build reads anything but
 * this, so the panel is a way to find the numbers and never a second copy of them.
 *
 * It is exported for the panel's sake alone. `DevGui.js` is imported only when the build is a
 * development one, so in a production bundle nothing imports this and it stays what it looks like:
 * the row's own configuration, held as data.
 *
 * ---
 *
 * `posX`, `posY`, `posZ` — where the row sits, as an offset from the plane the camera faces. The
 *   row is laid out about its own local origin, which is the middle of the bar and not its left
 *   end, so at zero it is centred on the screen and level with the coin's own height. `posZ` is
 *   towards the camera: positive brings the bar nearer and makes it read larger.
 *
 * `rotX`, `rotY`, `rotZ` — the row's turn within the camera's frame, in degrees, applied in that
 *   order. All zero is a bar lying flat in the plane of the screen. `rotY` is the one that used to
 *   be hard-coded at twelve degrees, which read the row as a shelf going back into the view; the
 *   bend below is what does that job now, and it does it without the taper the yaw dragged across
 *   the plates — twelve degrees drew the plate beside the coin 9.6% wider than flat, 176px against
 *   160 at 1920x1080, which is the whole of why the row's near half used to crowd the coin.
 *
 * `scale` — one multiplier over the row's size. `type` below sets the size against the coin; this is
 *   on top of it, for the day the row wants to be a little smaller without the coin moving. The
 *   press set-back is a world distance and is corrected for this — see `updateNavBar`.
 *
 * `bend` — how far the bar curves towards the viewer, as the total sweep of its arc in degrees,
 *   zero being dead flat. The arc's apex is the row's centre and the two ends sweep back, so the
 *   first and last buttons — Caricatures and VR — are the furthest from the eye and the middle is
 *   the nearest, which is a bar that has wrapped very slightly towards the person looking at it
 *   rather than one laid at an angle they see edge-on. Each button also turns to sit square on its
 *   own bit of the arc, so the bend is what gives every plate its own small angle, and the wall
 *   shading reads off that same angle rather than off a fixed constant. Fourteen degrees puts the
 *   ends about 25px further back than the middle at 1920x1080; sixty is a hoop and zero is a ruler.
 *
 * ---
 *
 * The plate's own numbers, which the panel also drives because they were tuned by eye and are
 * worth being able to tune again without an edit each time. `thickness` and `corner` are geometry
 * and rebuild the plates when they change — see `rebuildPlates`, which is why they are allowed to
 * be live at all; `type`, `first` and `gap` are layout and cost nothing.
 *
 * `type` — type height as a share of the coin's radius. 0.58 was the bare row's size and is too big
 *   once every word carries a plate: at 1920x1080 the plated row at that size ran 985px from the
 *   coin's edge and past the middle of the screen. 0.29 brought the whole row to about 540px, 0.36
 *   to 648px, and 0.45 to 811px. The row is not proportional to this number, and the reason is
 *   worth keeping straight, since getting it wrong is what put an early set of these figures out by
 *   about a seventh: the five gaps between the plates are the coin's radius times `gap` — not `gap`
 *   of anything this number scales — so they stay the same size whatever the type does, 96px of the
 *   811, at every cut above. Only the 715px of plate beside them follows this number.
 *
 * `thickness` — the plate, front face to back. 0.46 drew a wall band of a half to a pixel and a
 *   half, a hairline, and is most of why the plates used to read as flat cut-outs. 1.4 draws 4.3px
 *   at the plate beside the middle, 0.7px at the one the eye is edge on to, and 4.6px at the far
 *   end, against a rim 4.1px wide.
 *
 * `corner` — the plate's corner radius. The tray's take this less `RIM_W`, so the band keeps one
 *   width, and that only holds while this stays above `RIM_W`. It is bounded above by the plates'
 *   own half-height, 1.32 — every plate is now the tallest word's height, not its own — so a slider
 *   that runs to 1.2 stays clear of rounding the plate into a lozenge.
 *
 * `gap` — the space between two plates, as a share of the coin's radius rather than of the type. The
 *   coin's radius is itself a fraction of the view, so this is that fraction twice over and the row
 *   keeps its spacing at every aspect.
 *
 * `first` — the space between the coin and the head word when it has come out, the same share of the
 *   coin's radius. It is not the row's first gap any more: the row is centred on the screen and the
 *   coin is a corner of it, so the row's left end is nowhere near the coin and the word travels
 *   between the two on its own. This is where it comes to rest, a coin's radius and a bit past the
 *   coin's trailing edge, so the slider is the word's resting distance and nothing else.
 *
 * `setback` — a press, as a share of the row's distance from the camera. The row sits four units
 *   out, so a set-back measured in button heights would be a fraction of a pixel; eight per cent of
 *   the depth is 0.32 units and a step you can see.
 */
export const navTweaks = {
    posX: 0,
    posY: 0,
    posZ: 0,
    rotX: 0,
    rotY: 0,
    rotZ: 0,
    scale: 1,
    bend: 14,
    type: 0.45,
    thickness: 1.4,
    corner: 0.45,
    gap: 0.42,
    first: 0.75,
    setback: 0.08,
};

/** What `navTweaks` is at rest, for the panel's reset. Read off the object above so the two can
 *  never disagree about what the shipped numbers are. */
export const NAV_TWEAK_DEFAULTS = Object.freeze({ ...navTweaks });

/**
 * Idle, under the pointer, and where the camera actually is — each in the four surfaces a
 * button is made of.
 *
 * The word is still the artist's own red, and that is the one thing the lobby did not change:
 * `Assets/Images/logo-stamp.png` is 794x794 and sampled over its opaque pixels it is two colours
 * and not one — `#645075` is 74% of the artwork and `#FF0006` 2.6%, the rest being the two
 * antialiased into each other — and the word keeps the red so the row still reads with the coin
 * beside it. The head word keeps the violet for its sides (`LOGO_VIOLET` below), because the head
 * word is the label the logo carries and the planet labels are cut to match it. The plates do not
 * keep it, and that is the deliberate break here.
 *
 * The plate and its tray used to be that same violet, and are now the lobby's own furniture: a
 * gunmetal panel with the doorway's green along its edge. The gate is the one thing the visitor has
 * already stood in front of by the time this row appears, it is dark metal, and the light in it is
 * green — four `PointLight`s of exactly `new THREE.Color('green')` at the doorway, added by
 * `LobbyContainer`'s `addPointLights`. A violet row hung over that room was the one thing in it
 * belonging to another picture.
 *
 * `seam` is what the plate's *wall* is drawn in — the second material every extrusion here carries
 * — and that wall is the whole visible edge of the plate: `ExtrudeGeometry` puts the outer edge and
 * the tray's inner edge in one material group, so a single green band outlines the panel and the
 * window cut in it. It is still shaded per plate like every other wall here (see `wallOf` and
 * `WALL_DARK`), so the seam dims towards the row's ends as the bend turns them away from the eye.
 * That is what keeps it a lit edge rather than a sticker laid on one.
 *
 * `field` is the recess, and it is black glass rather than a tint of the plate. The tray is real
 * geometry — see `buildButton` — and the bend turns every plate a little out of the camera's plane,
 * but the tray is a step of a fifth of a unit inside a plate scaled down to a share of the coin,
 * which at the shallowest angle the bend gives is still too short a wall to find by eye, so the
 * floor has to say "lower" as a colour.
 *
 * The rest are these lifted: white for the button under the pointer, which is the loudest thing a
 * word can say and is what the rest of the site uses for a hover; a brighter green for the seam;
 * and a tint of each for the category the camera is actually on, which cannot be told from its
 * neighbours at the idle tone.
 */
const IDLE = { word: 0xff0006, plate: 0x3a3f3c, field: 0x0d100e, seam: 0x4aff8a };
const HOVER = { word: 0xffffff, plate: 0x5a615c, field: 0x161a17, seam: 0x8cffb4 };
const ACTIVE = { word: 0xff6a6d, plate: 0x474d49, field: 0x121613, seam: 0x4aff8a };

/**
 * The wall of anything above: its own face colour, this far into its own shadow — and the two ends
 * of the range the plate's own outer wall is shaded across.
 *
 * Everything in the HUD is `MeshBasicMaterial`, which is unlit: a wall's tone is whatever we write
 * on it and never what the scene's lights would do to it. For a face that is fine and for a wall it
 * is the whole complaint — a plate turned away from the camera drew the identical flat side a plate
 * facing it head-on would, so the row read as six flat cut-outs laid at an angle rather than as six
 * solids standing at one.
 *
 * So the wall is shaded by hand, from how much of it the eye can see: `facing` is the cosine
 * between the plate's own right axis and the line from the eye to the plate — measured without a
 * sign, since the row crosses the eye's axis and the two walls are the same wall — and `wallOf`
 * takes it as a shade between these two. What counts as fully turned is not a constant but the
 * cosine of the angle at the row's own ends, which `updateNavBar` reads off the bend it is drawing:
 * the row's two ends are therefore always its darkest walls and the plate at the middle is always
 * the flattest, whatever the panel sets, and a flat bar shades nothing at all rather than shading
 * everything the same.
 *
 * The shading is per plate and not per face, and that is forced rather than chosen: the row crosses
 * the eye's own axis, so plates on one side of the crossing show their right wall and plates on the
 * other their left, the two are never both visible on one plate, and `ExtrudeGeometry` puts every
 * side wall in a single material group — there is no separate face to give a separate tone to.
 * One plate, one wall, one shade. It varies down the row, which is where the eye reads it.
 */
const WALL_SHADE = 0.72;
const WALL_DARK = 0.42;

/** Scratch for `wallOf`, which runs for every surface of every button on every frame. */
const wallScratch = new THREE.Color();

/**
 * The wall colour of a face colour, as the hex `MeshBasicMaterial` and `setHex` both take.
 *
 * `shade` defaults to the single value everything but the plates is built with — the words, whose
 * sides are a twelve-hundredth of a unit deep and show a band under a pixel wide, and anything
 * built once where there is no frame to shade from. The plates pass their own, per frame.
 */
function wallOf(hex, shade = WALL_SHADE) {
    return wallScratch.setHex(hex).multiplyScalar(shade).getHex();
}

/** The word shown beside the coin while the row is out. Its sides take the
 *  stamp's violet, which the plates used to wear and no longer do — see `LOGO_VIOLET`. */
const HINT_COLOR = 0xff0006;

/**
 * The stamp's violet, `#645075`: 74% of `Assets/Images/logo-stamp.png` and the colour this row's
 * plates were built in until they became the lobby's gunmetal.
 *
 * It is kept for one surface and one alone — the sides of the head word beside the coin. That word
 * is the logo's own, it is hung on the coin's own layer, and it is the thing the planet labels were
 * just cut to match; giving it the plate's new grey would have left the labels matching a word that
 * had changed under them.
 */
const LOGO_VIOLET = 0x645075;

/** The word shown beside the coin while the row is out. */
const HINT_TEXT = 'Categories';

/**
 * The site's other two pages, at the row's far end. Built exactly like the six categories —
 * same font, same plate, the same shared `plateH` — because this row is where the site's
 * navigation lives; only the action differs, and `press` is what answers it. Full-page moves,
 * not camera work: `href` is where the browser goes and the app does not come back.
 */
const SITE_LINKS = [
    { label: 'Modern', href: '../' },
    { label: 'Funny & Witty', href: '../funny-witty/' },
];

const raycaster = new THREE.Raycaster();
raycaster.layers.set(LAYER);
raycaster.layers.enable(LOGO_LAYER);

/** Scratch, at module scope because `updateNavBar` runs every frame. */
const pointer = new THREE.Vector2();
const RIGHT = new THREE.Vector3();
const UP = new THREE.Vector3();
const FORWARD = new THREE.Vector3();
const scratch = new THREE.Vector3();
/** The row's own right axis in world, at one button's place on the arc. Written per button in
 *  `updateNavBar`, because the bend gives every plate its own angle and so its own right axis —
 *  and that axis is what its wall is shaded against. */
const buttonRight = new THREE.Vector3();
/** The row's own turn within the camera's frame, from the panel's three numbers. Rewritten each
 *  frame; three Euler reads and a quaternion conversion are nothing beside the six buttons. */
const tweakEuler = new THREE.Euler();
const tweakRotation = new THREE.Quaternion();
/** The inverse of the row's own turn, and the coin's place in the row's own frame. The row lays
 *  itself out in that frame, but the coin is not in it — it is a corner of the camera's view, and
 *  the panel can turn the row out from under it. Both are written once a frame and then read by the
 *  six buttons and the head word, which is the whole reason they are at module scope. */
const worldToRow = new THREE.Quaternion();
const coinLocal = new THREE.Vector3();

/**
 * Builds the row, empty, and wires the pointer to it.
 *
 * The buttons themselves wait for the planets — `buildOnce` adds them the first frame the six
 * gallery categories can all be resolved to one — so this is called as soon as there is a scene
 * to add it to, exactly like the coin beside it.
 *
 * @param {THREE.Object3D} logoGroup - the coin. The row lays itself out from where the coin
 *   actually is each frame, and a press on the coin is what opens the row.
 * @param {(planet: THREE.Object3D) => void} onSelect - MainScene's `switchPlanet`.
 * @param {() => boolean} isLive - whether the row is a control yet. Handed in rather than asked
 *   for: the answer belongs to MainScene, which owns the lobby and the doors, and the coin is
 *   furniture until they are open.
 * @param {() => void} onHome - MainScene's `goBack`, run when the coin shuts the row. Handing the
 *   function over rather than a flag is the same choice `isLive` makes: whether shutting the row
 *   should also send the camera home is MainScene's to answer, and what "home" means changes with
 *   what is open. Nothing in this file knows either.
 */
export function createNavBar(logoGroup, onSelect, isLive, onHome) {
    const root = new THREE.Group();
    root.name = 'navBar';

    const font = new FontLoader().parse(droidSans);

    const hint = buildLabel(font, HINT_TEXT, HINT_COLOR, LOGO_VIOLET);
    hint.visible = false;
    root.add(hint);

    root.userData = {
        logoGroup,
        onSelect,
        onHome,
        isLive,
        font,
        hint,
        buttons: [],
        /** What `pick` is handed while the row is out, and while it is not. Two arrays, built
         *  once: a mousemove builds nothing. The head word is in both of them — it is the toggle's
         *  own label and, in a narrow frame, the column's head, so it is a thing to press in either
         *  form — and `pick` is what keeps it out of the ray on the frames where it is hidden. */
        targets: [logoGroup, hint],
        logoTarget: [logoGroup, hint],
        open: false,
        /** How far the row has come out, for the left-to-right reveal: 0 is away, 1 is fully out.
         *  Eased at `REVEAL_HALF_MS`, and the buttons are staggered along it — see `updateNavBar`.
         *  It is a second value rather than a use of `open` because `open` is what the pointer and
         *  the ray care about and this is only how it looks; the two are never the same number. */
        reveal: 0,
        /** Which of the row's two forms the last frame was drawn in — `updateNavBar`'s fit test,
         *  kept so the pointer can read it back. A press runs off an event and has no camera to ask,
         *  and the form is what decides whether that press puts the column away; the frame old is
         *  the frame the press was made on, which is the answer wanted. */
        stacked: false,
        /** Whether the column was up last frame, so the Legend is shut once on the way out rather
         *  than every frame it is up. A transition and not a state, because nothing here may *open*
         *  the Legend: which list belongs on the screen is `LobbyContainer`'s answer, and this file
         *  does not know which part of the page it is looking at. */
        columnOut: false,
        hovered: null,
        /** The admin door — Neptune's plate (label ADMIN, Planets.js). Outside `buttons`, so the
         *  row's arc loop never lays it out; built in `buildOnce`, shown only while Neptune is
         *  the focused planet, at the centre of the frame. See `updateNavBar`. */
        door: null,
        /** How far the head word has come out from behind the coin: 0 in behind it, 1 at the coin's
         *  trailing edge plus `navTweaks.first`. Eased the same way the press is, at `HINT_HALF_MS`.
         *  It is on its way to 1 exactly while the row is open — one travel for both forms, and only
         *  where it rests differs, see `ACROSS_FIT`. */
        hintOut: 0,
        /** The button the pointer is down on, or `null`. Set by `swallow`, cleared by `release`,
         *  and held across frames — which is the whole of "down" and "up" as a state. */
        pressed: null,
        /** What the current press *began* on — a button or the coin — and where the pointer was
         *  when it began. Set by `swallow` and spent by `press`, which is why it outlives
         *  `pressed`: `release` clears that one first, and this has to survive until the click.
         *  See `press` for why the click must not ask the ray a second time. */
        armed: null,
        armedX: 0,
        armedY: 0,
        /** The last frame's clock, for `PRESS_HALF_MS`. Seeded rather than left undefined: the
         *  first frame's delta would otherwise be `NaN` and every plate would be `NaN` through
         *  the tray. */
        lastAt: performance.now(),
        /** What the plates' geometry was last cut at. `thickness` and `corner` are the two numbers
         *  the panel drives that a material cannot carry, so a change to either has to be paid for
         *  with a new geometry — this is what tells `updateNavBar` a change has happened without
         *  the panel having to say so, which keeps the panel's only job writing to `navTweaks`. */
        built: { thickness: navTweaks.thickness, corner: navTweaks.corner },
    };

    addListeners(root);

    return root;
}

/**
 * Puts the row across the top of whatever camera is live, and marks which category the camera is
 * on.
 *
 * The depth of the plane and the height it sits at are read from the coin rather than recomputed:
 * the coin's own placement is `CornerLogo`'s business (same plane, same corner arithmetic), and it
 * is written before this runs — see `animate`. So there is no second copy of those numbers here to
 * fall out of step with the first.
 *
 * Where the row goes *across* the screen no longer comes off the coin. It used to: the row began at
 * the coin's leading edge, and the coin's own corner arithmetic placed it. The row is laid out about
 * its own middle now and sits centred, which is the arrangement asked for — the mark alone in the
 * corner, the bar through the middle of the top. That also ends the one thing the old arrangement
 * was for: the row used to start where the head word ends, so opening it read as the bar coming out
 * from behind the word. It does not any more, and the word is left where it was, at the coin.
 *
 * A button's place is its arc-length position along a curve rather than a distance along a straight
 * line: the row is bent towards the viewer by `navTweaks.bend`, so the middle of the bar is its
 * nearest point and the two ends sweep back. Each button also turns to sit square on its own bit of
 * that arc, which is where every plate's own small angle comes from — and the wall shading below
 * reads off that same angle.
 *
 * @param {THREE.Group} navBar - what `createNavBar` returned.
 * @param {THREE.Camera} camera - the live camera, as `updateCornerLogo` takes it.
 */
export function updateNavBar(navBar, camera) {
    if (!navBar || !camera) return;

    const state = navBar.userData;
    const logo = state.logoGroup;

    if (!logo) return;

    buildOnce(navBar);

    // The camera's own axes, in the world — the same three reads `updateCornerLogo` makes, and
    // sound for the same reason: the rig the camera hangs from is never given a rotation.
    RIGHT.set(1, 0, 0).applyQuaternion(camera.quaternion);
    UP.set(0, 1, 0).applyQuaternion(camera.quaternion);
    FORWARD.set(0, 0, -1).applyQuaternion(camera.quaternion);

    // Where the coin is, in the camera's own frame. Its depth along the view axis is the plane the
    // row is laid out *on*; `coinY` is the height the row's own origin is placed at, so that at rest
    // the row is level with the coin.
    scratch.copy(logo.position).sub(camera.position);

    const depth = scratch.dot(FORWARD);
    const coinY = scratch.dot(UP);
    const coinR = logo.scale.x;

    // The row's turn, from the panel's three numbers in degrees — rebuilt from them rather than
    // accumulated, so the sliders are the whole of the state and nothing here remembers an angle of
    // its own. `XYZ` is the order that reads as the row's height, then its width, then its depth.
    //
    // Before the quaternion below and not after: this multiplies into it, so computing it on the
    // line after would put the *previous* frame's turn into this frame's placement, and the row
    // would lag a drag by one frame for no reason at all.
    tweakRotation.setFromEuler(tweakEuler.set(
        THREE.MathUtils.degToRad(navTweaks.rotX),
        THREE.MathUtils.degToRad(navTweaks.rotY),
        THREE.MathUtils.degToRad(navTweaks.rotZ),
    ));

    // The row's own origin is its middle and not its start, and it sits at the middle of the screen
    // unless the panel has moved it. `posZ` is towards the camera, so it is added to the depth the
    // row was going to sit at rather than subtracted from it.
    navBar.position.copy(camera.position)
        .addScaledVector(FORWARD, depth + navTweaks.posZ)
        .addScaledVector(RIGHT, navTweaks.posX)
        .addScaledVector(UP, coinY + navTweaks.posY);

    // The camera's own orientation, then the row's turn within it. Applied in that order because a
    // quaternion multiplication runs the right-hand one first: the turn is about the row's own axes,
    // and those only become *world* directions once the camera's rotation has been put on top of
    // them.
    navBar.quaternion.copy(camera.quaternion).multiply(tweakRotation);

    navBar.scale.setScalar(navTweaks.scale);

    // Into the row's own frame, for the one thing that is positioned there but does not belong to
    // it: the head word, which lives at the coin. Everything else here is written in this frame
    // because it moves with the row; the word is pinned to a thing outside it, so the row's own
    // transform has to be undone before it can be placed.
    //
    // The coin is a child of the HUD scene and nothing else, so its world position is its position;
    // the difference between the two is a world vector, the inverse turn takes it into the row's
    // axes, and dividing by the row's own scale takes it the rest of the way into the units the
    // buttons are written in. `coinLocal.y` is therefore the row's own height of the coin, which is
    // what the buttons sit at — and zero at rest, since the origin above is already at that height.
    worldToRow.copy(navBar.quaternion).invert();

    coinLocal.copy(logo.position)
        .sub(navBar.position)
        .applyQuaternion(worldToRow)
        .divideScalar(navTweaks.scale);

    const unit = coinR * navTweaks.type;
    const active = getFocusedPlanet();
    const buttons = state.buttons;

    // The row's own arc length, which is what the bend is a bend *of*. Measured rather than kept,
    // because the panel changes the type size and the gaps under it at any moment: six additions a
    // frame against a stored length that is right until somebody moves a slider.
    let rowLength = 0;

    for (let i = 0; i < buttons.length; i++) {
        rowLength += buttons[i].userData.width * unit + (i > 0 ? coinR * navTweaks.gap : 0);
    }

    // The bend, in the numbers the arc is actually drawn from. It is given as a total sweep, so a
    // half-sweep is the angle at each end and the arc's radius follows from the row's own length —
    // which is what keeps the curve looking the same when the type size changes it. Zero is a
    // straight bar and not a divide by zero, so it is answered separately.
    const sweep = THREE.MathUtils.degToRad(navTweaks.bend) / 2;
    const arcRadius = sweep > 1e-4 ? rowLength / (sweep * 2) : 0;

    // How much angle counts as a fully-shaded wall. It is the angle at the row's own ends and not a
    // constant, so the darkest wall the row draws is always the wall at its two ends and the middle
    // plate is always the flattest — the shading follows the bend the panel sets instead of a number
    // that was true of the bend it was measured at.
    const facingFull = Math.max(0.05, Math.sin(sweep));

    // Whether the row is a row at all this frame, or the column a narrow frame gets instead. Asked
    // here because this is the one place that has both numbers: the row's own length, above, and the
    // frame it has to be drawn in. `posZ` is on the depth for the reason it is on the placement — the
    // row sits nearer the camera by it, and the frame is wider the nearer it comes.
    //
    // Recomputed every frame rather than remembered, like everything else about this row: a window
    // that is dragged is a stream of new frames, and a form that switched on a resize event would be
    // a second copy of this answering about a frame that had already gone.
    const frameHalfWidth = (depth + navTweaks.posZ)
        * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * camera.aspect;
    const stacked = rowLength * navTweaks.scale / 2 > frameHalfWidth * ACROSS_FIT;

    // Kept for the pointer, which has no camera to ask — see `stacked` in `createNavBar` and the
    // press that reads it back.
    state.stacked = stacked;

    // The column comes down through the frame rather than running along its top, and the Legend is
    // a block sitting in that same frame, so the Legend goes while the column is up. Asked of both
    // numbers rather than of `stacked` alone, because a row across a wide frame is not in the
    // Legend's way and has no business shutting it.
    //
    // Shut on the frame it comes out and never opened from here. Which list belongs on the screen is
    // `LobbyContainer`'s answer — it opens the solar one on the landing — and this file knows only
    // that its own column is out. Nothing is stranded by it: the Legend's own button stays on the
    // screen either way, so a visitor who wants the list back has one press of it.
    const columnOut = stacked && state.open;

    if (columnOut && !state.columnOut) setLegend(null);

    state.columnOut = columnOut;

    // How much of the distance still to go the plates cover this frame. Worked out from the clock
    // rather than assumed to be a frame, so the press reads the same on a machine that is not
    // drawing at sixty — and an exponential is all this needs: it cannot overshoot, it cannot get
    // stuck short of the end, and a frame that took a second arrives at the end immediately, which
    // is what a tab that was in the background should do rather than creep.
    const now = performance.now();
    const since = now - state.lastAt;

    state.lastAt = now;

    const pressK = 1 - Math.pow(0.5, since / PRESS_HALF_MS);
    const hintK = 1 - Math.pow(0.5, since / HINT_HALF_MS);
    const revealK = 1 - Math.pow(0.5, since / REVEAL_HALF_MS);

    // The row's arrival, one number for the whole of it: the buttons below read off it rather than
    // each carrying an arrival of its own, so opening and shutting cannot get out of step and there
    // is nothing per button to reset when the row is built again.
    state.reveal += ((state.open ? 1 : 0) - state.reveal) * revealK;

    // The plates are cut again here rather than in a listener of their own, because this is the one
    // place that already runs once a frame and already knows what the panel last set. Comparing
    // against what was built is what makes it a rebuild *on change* — a drag asks a great many
    // times and only the frames where a number actually moved pay for it.
    if (state.built.thickness !== navTweaks.thickness || state.built.corner !== navTweaks.corner) {
        state.built.thickness = navTweaks.thickness;
        state.built.corner = navTweaks.corner;

        rebuildPlates(navBar);
    }

    // The column's own arithmetic, read only when `stacked`. The plates are one height — the tallest
    // word's, see `buildOnce` — so the six of them are one pitch and the column is uniform, and the
    // only numbers it needs are that pitch and how far below the head word the first plate starts.
    //
    // `stackTop` is measured from the head word's centre to the first plate's centre: half of each,
    // and one `gap` between them, which is the same gap the row puts between its plates and is read
    // off the coin for the same reason. The word is a `TextGeometry` centred on its own origin by
    // `buildLabel`, so half its height is what pays for the centring — the same half the lead
    // placement below spends on its width.
    const plateH = buttons.length ? buttons[0].userData.height * unit : 0;
    const stackStep = plateH + coinR * navTweaks.gap;
    const stackTop = (state.hint.userData.height * unit) / 2 + coinR * navTweaks.gap + plateH / 2;

    // Arc length from the row's own start, which is now its left end — the row is laid out about
    // its middle, so this counts up from -rowLength/2 once it is turned into a place on the arc.
    let cursor = 0;

    for (let i = 0; i < buttons.length; i++) {
        const button = buttons[i];
        const width = button.userData.width * unit;

        // How far out this plate is, between away and fully out — the row's one reveal spread across
        // the six by index. The first starts on the first frame and the last starts once
        // `REVEAL_STAGGER` of the sweep has gone, so the ends finish where they started: the whole
        // row is out exactly when the reveal reaches 1 and not before.
        const start = buttons.length > 1 ? (i / (buttons.length - 1)) * REVEAL_STAGGER : 0;
        const appear = Math.min(1, Math.max(0, (state.reveal - start) / (1 - REVEAL_STAGGER)));

        // Away is away, and not a plate scaled to nothing: a raycaster reaches an object with
        // `visible` false exactly as it reaches one on screen, which is why `pick` gates on
        // `state.open` — but a zero-scale plate is a degenerate matrix for the renderer to walk for
        // no reason, so it is taken out of the draw entirely.
        button.visible = appear > 0.001;

        // The row's size, which is `unit` — a button is built at unit size, a letter 1 tall, and
        // this is what makes it a share of the coin instead. It is set on the button and not on the
        // plate because the word has to be scaled with it: the plate is sized around the word, so
        // scaling one and not the other would take the word out of its tray.
        //
        // `appear` is folded into the same call rather than kept as a transform of its own, because
        // a plate that is growing *and* rising forward is one motion — held apart, the two could be
        // eased differently and disagree about where the plate is.
        button.scale.setScalar(unit * appear);

        // Where this button stands on the arc, measured from the row's middle rather than its end.
        // `theta` is its own angle off the row's normal, which is both where it stands and how far
        // it is turned — a point at angle `theta` on a circle whose centre is behind the row is
        // exactly a plate at `theta` turned to face out of that circle.
        // The column has no arc to stand on, so its plates are square to the camera — and `theta` at
        // zero is the whole of it: it takes the yaw below with it, and in the placement further down
        // both the arc's own offset and the reveal's set-back are read through `sin` and `1 - cos`,
        // which are nought and nought. One number is the difference between the two forms.
        const across = cursor + width / 2 - rowLength / 2;
        const theta = stacked ? 0 : (arcRadius > 0 ? across / arcRadius : 0);

        const sin = Math.sin(theta);
        const cos = Math.cos(theta);

        // The push, which is the pointer's to start and stop: the whole button, plate and word
        // together, back along its own normal — why that direction and not the ray from the eye is
        // in the `PRESS_HALF_MS` note above.
        //
        // No division here, and that is the one thing this line has to get right: the set-back is a
        // world distance and `button.position` is written in the *row's* frame, which is scaled by
        // `navTweaks.scale` alone. The plate used to take this offset instead, in the button's own
        // frame, scaled by `unit` — and that is where the `/ unit` beside it came from. Moving the
        // gesture up to the button without moving that division across would have multiplied the
        // set-back by twenty and driven the button back past the camera.
        const held = state.pressed === button ? 1 : 0;

        button.userData.press += (held - button.userData.press) * pressK;

        // Two things move a plate back along its own normal and they are added rather than blended:
        // the press, which is the pointer's, and the reveal, which is the plate not yet being out.
        // Both are shares of the row's depth and neither is scaled by the other — a plate pressed
        // while the row is still arriving is a plate that is both arriving and pressed, and the
        // second should not have to wait for the first to finish.
        const back = (button.userData.press * navTweaks.setback
            + (1 - appear) * REVEAL_RISE) * depth / navTweaks.scale;

        // At the row's own height, which is the coin's unless the panel's `posY` has lifted it — the
        // origin above was placed at that height on purpose. Across, y is written as a literal rather
        // than read off `coinLocal` because the two are answering different questions: the literal is
        // where the row is, and `coinLocal` is where the coin is in the row's frame, which is only
        // the same number while the row has not been moved. Reading it there would make `posY` a
        // no-op, the coin's local height moving down by exactly as much as the origin moves up.
        //
        // y is also the one axis the two forms do not share. Across, every plate is on the row's own
        // line — the arc bends them in x and z and leaves the line alone. In the column it is the
        // whole of the placement: the plates go down the screen from the head word, one pitch apart,
        // each centred on the row's own middle in x. And in the column it *is* read off `coinLocal.y`,
        // against the note above, because there the word is the column's head and the column hangs
        // from it — so the one height the six plates have to agree with the word about is the coin's.
        button.position.set(
            arcRadius * sin - back * sin,
            stacked ? coinLocal.y - stackTop - i * stackStep : 0,
            -arcRadius * (1 - cos) - back * cos,
        );

        // The plate stands square on its own bit of the arc: a face whose normal is the outward
        // direction at that point, which is a turn about the row's height by the same angle that
        // placed it.
        button.rotation.y = theta;

        // Where the button is out in the world, then the direction the eye sees it from — the same
        // read `updateCornerLogo` makes of the coin, and needed because the row is placed from the
        // camera and not parented to it. Taken after the position above, so the shade belongs to
        // where the button is this frame rather than to where it was last one.
        scratch.copy(button.position)
            .multiplyScalar(navTweaks.scale)
            .applyQuaternion(navBar.quaternion)
            .add(navBar.position)
            .sub(camera.position)
            .normalize();

        // How far off the plate's own right axis the eye stands, which is how much of a side wall
        // the plate shows. Absolute on purpose: the row crosses the eye's own axis as it curves, so
        // plates on one side of the crossing show their right wall and plates on the other their
        // left, and a wall is a wall either way — see `WALL_DARK`.
        buttonRight.set(cos, 0, -sin).applyQuaternion(navBar.quaternion);

        const facing = Math.abs(buttonRight.dot(scratch));
        const wall = WALL_DARK + (WALL_SHADE - WALL_DARK) * Math.min(1, facing / facingFull);

        const hovered = state.hovered === button;
        // `planet` is only on the category plates — the site-link plates at the far end have
        // none and are skipped here rather than read into.
        const here = active !== null && button.userData.planet &&
            button.userData.planet.name === active;
        const palette = hovered ? HOVER : (here ? ACTIVE : IDLE);

        button.userData.word.material[0].color.setHex(palette.word);
        button.userData.word.material[1].color.setHex(wallOf(palette.word));
        button.userData.rim.material[0].color.setHex(palette.plate);
        button.userData.rim.material[1].color.setHex(wallOf(palette.seam, wall));
        button.userData.field.material.color.setHex(palette.field);

        cursor += width + coinR * navTweaks.gap;
    }

    // ── The admin door ─────────────────────────────────────────────────────
    // Neptune is the admin entrance: its planet label reads ADMIN (Planets.js), and landing on
    // it shows one plate at the centre of the frame — the same hand-cut plate the categories
    // wear, the same shared plateH — whose press is a full-page move to admin.html. It is NOT in
    // `buttons` on purpose: the arc loop lays `buttons` along the row, and the door only exists
    // over Neptune, in the middle of the frame.
    if (state.door) {
        const door = state.door;
        const doorOn = active === 'Neptune';

        door.visible = doorOn;

        if (doorOn) {
            // The centre of the screen, in the row's own frame. The row's origin sits at the
            // coin's height (`coinY` along the camera's UP axis) and not on the view axis, so
            // the centre is that height taken back — divided by the row's scale, world units
            // into row units, exactly the division the press's set-back makes.
            const centerY = -(coinY + navTweaks.posY) / navTweaks.scale;

            // The press, the same easing the row's plates carry: sunk for as long as the pointer
            // is down — flat to the frame here, so the movement is along the row's own Z.
            const held = state.pressed === door ? 1 : 0;
            const pressK = 1 - Math.pow(0.5, since / PRESS_HALF_MS);
            door.userData.press += (held - door.userData.press) * pressK;
            const back = door.userData.press * navTweaks.setback * depth / navTweaks.scale;

            door.position.set(0, centerY, -back);
            door.rotation.y = 0;
            door.scale.setScalar(unit * 0.8);

            const hovered = state.hovered === door;
            const palette = hovered ? HOVER : IDLE;

            door.userData.word.material[0].color.setHex(palette.word);
            door.userData.word.material[1].color.setHex(wallOf(palette.word));
            door.userData.rim.material[0].color.setHex(palette.plate);
            door.userData.rim.material[1].color.setHex(wallOf(palette.seam));
            door.userData.field.material.color.setHex(palette.field);
        }
    }

    // The head word comes out with the row and at no other time. It used to come out under a passing
    // pointer — the coin hovered, the row still shut — and that is the one thing it no longer does: a
    // label that appears for a pointer that has pressed nothing is offering a control nobody asked
    // for, and on a touchscreen the pointer passing is a thing that never happens at all. What it
    // keeps is the slide: the word still travels left to right out from behind the mark, and the
    // column still takes it as its head.
    const out = state.open;

    state.hintOut += ((out ? 1 : 0) - state.hintOut) * hintK;

    // Hidden only once it is back out of sight. The way in and the way out are the same slide, and
    // `visible` false the instant the pointer leaves would cut the return off partway through.
    state.hint.visible = state.hintOut > 0.002;

    if (state.hint.visible) {
        const width = state.hint.userData.width * unit;

        // Placed by its LEADING edge. The word comes to rest just past the coin's trailing edge,
        // one `first` beyond it — that is the row's own version of "next to the coin" now that the
        // row itself is centred on the screen and its left end is nowhere near the coin. The word
        // and the row are two things on one line, and the only thing they share is the coin.
        //
        // It starts with that same edge at the COIN'S leading edge, so what the pointer brings on is
        // the word sliding out from behind the mark — its head behind the coin and its tail showing
        // past the coin's right side. The word is a coin and a half wide, so no placement hides all
        // of it, and this one puts the whole of what it cannot hide on the side it is travelling to.
        // (Behind the *coin*, and so behind its own width: at the coin's centre the tail would show
        // on one side and the head on the other, across the corner of the frame.)
        //
        // Both ends are read off `coinLocal`, the coin's place in the row's own frame, so the panel
        // can turn and scale the row without dragging the word off the logo — and `coinLocalR` is
        // the coin's radius in that same frame, since the coin is outside the row's scale and its
        // radius has to come inside. A `TextGeometry` is centred on its own origin by `buildLabel`,
        // so half its width is what pays for the centring.
        const coinLocalR = coinR / navTweaks.scale;
        const hiddenLeadX = coinLocal.x - coinLocalR;
        const shownLeadX = coinLocal.x + coinLocalR + coinLocalR * navTweaks.first;
        const leadX = hiddenLeadX + (shownLeadX - hiddenLeadX) * state.hintOut;

        // The depth is in the row's own units and not the word's: the set-back is a world distance,
        // and this position is written in the frame of the row, which is scaled by the row's own
        // scale — so it is divided by that, exactly as the press is above.
        state.hint.scale.setScalar(unit);
        // x is the one thing the two forms place differently: against the coin the word is pinned to
        // a thing outside the row and is placed by its leading edge, and over the column it is the
        // head of it and sits on the row's own middle — the same middle the plates below are centred
        // on. Its height and depth are the same in both.
        state.hint.position.set(
            stacked ? 0 : leadX + width / 2,
            coinLocal.y,
            -HINT_SETBACK * depth * (1 - state.hintOut) / navTweaks.scale,
        );
    }
}

/**
 * Adds the six buttons, once, when there is a planet for every gallery category.
 *
 * The scene is built asynchronously — `loadCelestialBodies` puts all nine planets in at the end
 * of its awaits — so this is asked on every frame until it can answer, and answers with nothing
 * until then. The test is that all six resolve rather than that the group is non-empty, because
 * the buttons are built from the mapping and the mapping is what would be half-built.
 */
function buildOnce(navBar) {
    const state = navBar.userData;

    if (state.buttons.length > 0) return;

    const items = CATEGORIES.map(category => ({
        label: category.label,
        planet: planets.children.find(planet => categoryFor(planet.name) === category.key),
    }));

    if (items.some(item => !item.planet)) return;

    // The words first and all of them, because the plates share one height and it is the tallest
    // word's — see the `PAD_X`/`PAD_Y` note. Cutting them here rather than inside `buildButton`
    // also means each is cut once: the button is handed the word it wraps instead of making a
    // second one to measure.
    const words = items.map(item =>
        buildLabel(state.font, item.label, IDLE.word, wallOf(IDLE.word), FONT_SIZE * WORD_DEPTH));

    const plateH = Math.max(...words.map(word => word.userData.height)) + PAD_Y * 2;

    items.forEach((item, i) => {
        const button = buildButton(words[i], plateH);

        button.userData.planet = item.planet;

        state.buttons.push(button);
        navBar.add(button);
    });

    // The site's other two pages, after the six categories. Cut from the same font and wrapped in
    // the same plate at the same shared `plateH` the categories set, so the row reads as one hand
    // all the way along. They carry `site` where the categories carry `planet` — see `press`.
    SITE_LINKS.forEach(link => {
        const word = buildLabel(state.font, link.label, IDLE.word, wallOf(IDLE.word), FONT_SIZE * WORD_DEPTH);
        const button = buildButton(word, plateH);

        button.userData.site = link;

        state.buttons.push(button);
        navBar.add(button);
    });

    // Built here rather than in `createNavBar`: the buttons are not there to name yet, and this
    // is the one moment the list stops growing.
    state.targets = state.buttons.concat(state.logoTarget);

    // The admin door, after everything else stops being counted into the row. Neptune is the
    // site's admin entrance: its planet label reads ADMIN (Planets.js), and landing on it shows
    // one plate at the centre of the frame — the same hand-cut plate the categories wear, the
    // same shared `plateH` — whose press is a full-page move to admin.html. It is deliberately
    // NOT in `buttons`: the arc loop above lays `buttons` out along the row, and the door only
    // ever exists over Neptune, in the middle of the frame.
    const adminWord = buildLabel(state.font, 'ADMIN', IDLE.word, wallOf(IDLE.word), FONT_SIZE * WORD_DEPTH);
    const door = buildButton(adminWord, plateH);

    door.userData.site = { label: 'ADMIN', href: '../admin.html' };
    door.visible = false;

    state.door = door;
    navBar.add(door);
}

/**
 * One button: a plate with a tray cut into it, and the word lying in the tray.
 *
 * The word arrives already cut, and the height arrives separately, because the row builds all six
 * words before it can know which is tallest and so how tall every plate is — see `buildOnce`. This
 * one function is why the row's plates are one size and its words are not.
 *
 * A group inside a group: the plate is a rim and a floor and the word is a sibling of it, and the
 * press moves the button whole — both go back together (see `updateNavBar`). The plate is a group
 * of its own only so the two meshes inside it can be added at once, not for anything the press
 * does to it; the colour changes are written straight onto the meshes, whose references
 * `userData` carries.
 *
 * The tray is a frame and a floor: the plate is one extruded rectangle with a rectangle cut out
 * of it, and a box fills the hole at a floor below it. A `Shape` hole is the cheap way to a
 * raised rim — the alternative is a boolean on two solids, which three does not have — and it
 * costs nothing here, because the hole is the tray and not the letters. That distinction is the
 * whole of the difference between this and cutting the words out of the plate: a hole in the
 * shape of a word would have to be built from its glyph outlines, and the word would then be a
 * window rather than a mark.
 */
function buildButton(word, height) {
    const width = word.userData.width + PAD_X * 2;
    const innerW = width - RIM_W * 2;
    const innerH = height - RIM_W * 2;

    const rim = buildFrame(width, height, innerW, innerH);
    const field = buildFloor(innerW, innerH);

    const plate = new THREE.Group();
    plate.add(rim, field);

    // The word's face ends up `WORD_LIFT` above the floor, and so `RIM_H` minus its own depth
    // minus that lift below the rim's top. `buildLabel` centres a letter on its own origin, so
    // half the extrusion is what pays for the centring here.
    word.position.z = -RIM_H + WORD_LIFT + (FONT_SIZE * WORD_DEPTH) / 2;

    const button = new THREE.Group();
    button.add(plate, word);

    // `innerW`/`innerH` are carried as well as the two meshes, because they are what `rebuildPlates`
    // needs to cut the tray again when the panel changes the thickness or the corner — and the tray
    // is the one part of the button that is not a property of its mesh.
    button.userData = { width, height, innerW, innerH, word, rim, field, press: 0 };

    // The whole subtree, group included: the pointer's raycaster is masked to this layer, and a
    // mask test that fails on the group skips every child under it whether they would pass or not.
    button.traverse(object => object.layers.set(LAYER));

    return button;
}

/**
 * The plate itself, as one extruded frame: the outer rounded rectangle with the tray cut out of it.
 *
 * Extruded the plate's full thickness and then translated, because `ExtrudeGeometry` runs its
 * depth the wrong way for this: the front of the plate has to be the plane the tray is measured
 * from, and the body has to be behind it, and the extrusion gives z from 0 out to the depth.
 *
 * The geometry is built apart from the mesh that wears it because the thickness and the corner
 * radius are live — the dev panel drives them, and see `rebuildPlates` for the swap. Materials
 * survive that swap; only this does not.
 */
function frameGeometry(width, height, innerW, innerH) {
    const hw = width / 2;
    const hh = height / 2;

    // The tray's corners are the plate's less exactly the band's width, and that is what holds the
    // rim at one thickness the whole way round: round both by the same radius and the band narrows
    // into every corner; round only the outside and it fattens into them.
    const innerR = Math.max(0, navTweaks.corner - RIM_W);

    const shape = new THREE.Shape();
    roundedRect(shape, hw, hh, navTweaks.corner);

    // Nothing here has to be wound against the outer shape. Membership of this array is what makes
    // it a hole, and `ExtrudeGeometry` reverses whichever of the two contours is the wrong way
    // round before it triangulates — so both are written by the one function.
    const hole = new THREE.Path();
    roundedRect(hole, innerW / 2, innerH / 2, innerR);

    shape.holes.push(hole);

    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: navTweaks.thickness,
        bevelEnabled: false,
        curveSegments: CURVE_SEGMENTS,
    });

    geometry.translate(0, 0, -navTweaks.thickness);

    return geometry;
}

function buildFrame(width, height, innerW, innerH) {
    // Two materials for the reason the letters carry two: an extrusion is cut into groups — the
    // faces first, then the sides it was extruded through — and on a material that takes no
    // light that second band is the only thing that separates a face from a wall.
    return new THREE.Mesh(frameGeometry(width, height, innerW, innerH), [
        new THREE.MeshBasicMaterial({ color: IDLE.plate }),
        new THREE.MeshBasicMaterial({ color: wallOf(IDLE.seam) }),
    ]);
}

/**
 * One rounded rectangle, added to a `Shape` or a `Path`, anticlockwise from the right edge.
 *
 * Four arcs and four lines, which is the whole of it. `absarc` takes the centre of the corner and
 * the two angles that bound it, and it draws the straight run to the arc's start itself — so the
 * edges between corners are the only lines worth writing, and the right edge is not even one of
 * those: `closePath` carries it, back up to where the first arc began.
 *
 * `CURVE_SEGMENTS` cuts these arcs too, which is coarse for a circle and is nothing here: at the
 * row's size the corner is about nine pixels across, and a quarter of it cut in three steps is a
 * third of a pixel off the true arc.
 */
function roundedRect(path, hw, hh, r) {
    path.moveTo(hw, -hh + r);

    // The arcs run clockwise one corner at a time, which leaves the outline itself anticlockwise —
    // up the right edge, along the top to the left, down the left, back along the bottom.
    path.absarc(hw - r, -hh + r, r, 0, -Math.PI / 2, true);
    path.lineTo(-hw + r, -hh);

    path.absarc(-hw + r, -hh + r, r, -Math.PI / 2, -Math.PI, true);
    path.lineTo(-hw, hh - r);

    path.absarc(-hw + r, hh - r, r, Math.PI, Math.PI / 2, true);
    path.lineTo(hw - r, hh);

    path.absarc(hw - r, hh - r, r, Math.PI / 2, 0, true);

    path.closePath();
}

/**
 * The tray floor, as a box: the slab that fills the frame's hole and carries the word.
 *
 * A box and not a plane, so the tray has a back and the plate keeps a solid silhouette at the
 * oblique angles the outer buttons are seen at.
 *
 * Split from its mesh for the same reason the frame is, and it is the one part of the plate whose
 * position also moves with the thickness: the floor's own face is pinned at `-RIM_H` and only its
 * back follows the plate, so a thicker plate is a deeper tray and not a word that has sunk.
 */
function floorGeometry(innerW, innerH) {
    // Floored rather than passed straight through, because the panel can drive `thickness` below
    // `RIM_H` and a box with a negative depth is inside out — its faces point the wrong way and it
    // renders as a slab of nothing. The clamp is a tenth of a unit, which no plate is ever that
    // thin and no eye could tell from zero.
    return new THREE.BoxGeometry(innerW, innerH, Math.max(0.1, navTweaks.thickness - RIM_H));
}

function buildFloor(innerW, innerH) {
    const mesh = new THREE.Mesh(floorGeometry(innerW, innerH),
        new THREE.MeshBasicMaterial({ color: IDLE.field }));

    // Floor at -RIM_H, back of the plate at -thickness, so the middle of the two is its centre.
    mesh.position.z = -(RIM_H + navTweaks.thickness) / 2;

    return mesh;
}

/**
 * Cuts the plates again, when the panel has changed something a plate is made of.
 *
 * Only two numbers reach here — `thickness` and `corner` — and they are the two that cannot be
 * answered by a transform, which is why they are the only ones that cost anything: a thickness is
 * an extrusion depth and a corner is a shape's radius, and neither exists until the geometry is
 * built. Everything else the panel drives is layout, read fresh each frame.
 *
 * The words are left alone. A word's geometry depends on the type size and its own letters and on
 * neither of the two numbers above, so re-cutting six of them on every frame of a slider drag would
 * be the expensive half of the work for no change at all. Materials are left alone too: only the
 * geometry is replaced, and the old one is disposed of as it goes, because a slider drag is a great
 * many rebuilds and geometry that is dropped without being disposed of is leaked on the GPU.
 */
function rebuildPlates(navBar) {
    // The admin door's plate is cut from the same two numbers the panel drives, so it rebuilds
    // with the row even though it does not live on it.
    const buttons = allPlates(navBar);

    buttons.forEach(button => {
        const { width, height, innerW, innerH, rim, field } = button.userData;

        rim.geometry.dispose();
        rim.geometry = frameGeometry(width, height, innerW, innerH);

        field.geometry.dispose();
        field.geometry = floorGeometry(innerW, innerH);
        field.position.z = -(RIM_H + navTweaks.thickness) / 2;
    });
}

/** The row's plates plus the admin door — everything `buildButton` cut, for `rebuildPlates` and
 *  nothing else. */
function allPlates(navBar) {
    const state = navBar.userData;

    return state.door ? state.buttons.concat([state.door]) : state.buttons;
}

/**
 * One flat-cut word, centred on its own origin, at unit size.
 *
 * The centring is `Planets.createText`'s `centerGeometry`, and the font is the same Droid Sans
 * the planet labels are cut from — the two rows are read together, so they are the same hand.
 * What differs is the rest: this takes no light at all — two flat `MeshBasicMaterial`s rather
 * than the `MeshPhongMaterial` a label wears — because the row is a HUD over a scene whose
 * spotlight moves under it, and it has to read at the same brightness whatever that light is
 * doing. It also carries no `position.setY(radius + 2)`, that lift being a label's relation to
 * the planet it names and not a row's to the frame.
 *
 * Two materials because `ExtrudeGeometry` cuts a letter into two groups — the faces, then the
 * sides it was extruded through — and on a material that takes no light the band of the second
 * is the only thing that says a letter has any thickness at all.
 *
 * `depth` is the caller's, because the two words here want different ones: the button's lies in
 * a tray and is a shallow relief, while the head word stands on nothing and keeps the deep cut.
 *
 * Both dimensions are left on `userData` because the plate is built around them and the bounding
 * box has been spent by then — the translate below moves the geometry, so a box read afterwards
 * would be a box around the moved mesh.
 */
function buildLabel(font, text, faceColor, sideColor, depth = FONT_SIZE * DEPTH_RATIO) {
    const geometry = new TextGeometry(text, {
        font,
        size: FONT_SIZE,
        // `depth`, not `height`: three renamed the extrusion parameter in r132 and the old name
        // is not read at all — a lesson Planets.js carries a comment about.
        depth,
        curveSegments: CURVE_SEGMENTS,
        bevelThickness: BEVEL,
        bevelSize: BEVEL,
        bevelEnabled: true,
    });

    geometry.computeBoundingBox();

    const box = geometry.boundingBox;

    geometry.translate(
        -(box.max.x + box.min.x) / 2,
        -(box.max.y + box.min.y) / 2,
        -(box.max.z + box.min.z) / 2,
    );

    const mesh = new THREE.Mesh(geometry, [
        new THREE.MeshBasicMaterial({ color: faceColor }),
        new THREE.MeshBasicMaterial({ color: sideColor }),
    ]);

    mesh.userData.width = box.max.x - box.min.x;
    mesh.userData.height = box.max.y - box.min.y;
    mesh.layers.set(LAYER);

    return mesh;
}

/* ── The pointer ─────────────────────────────────────────────────────────────── */

/**
 * How far the pointer may travel between the press and the click and still count as the same press,
 * in screen pixels.
 *
 * It is the slop a browser gives a drag before it stops meaning a click, and it exists because the
 * click acts on where the press *began* rather than on what is under the cursor when it ends: a
 * press that has moved further than this is a drag, and a drag off a button is nobody's — see
 * `press`.
 */
const DRAG_SLOP = 8;

/**
 * Watches the window, in the capture phase, so a press that lands on the row is the row's before
 * it is the canvas's.
 *
 * Both halves matter and for different reasons. `mousemove` is watched only to say what is under
 * the pointer — the coin lights, a button brightens — and it is deliberately **not** swallowed:
 * the canvas has its own hover and its own pointer bookkeeping, and a pointer that happens to be
 * over the corner is still a pointer the rest of the scene may need to know about.
 *
 * `mousedown` and `click` **are** swallowed, and this is not tidiness. MainScene's click handler
 * is on the canvas and would otherwise run for a button press too — reading the same screen point
 * as a ray into the scene, which on a planet is a ray into that planet's ring, and would select
 * or deselect an artwork behind the word that was actually clicked. Capture is what makes that
 * guarantee hold whatever the canvas does with the event afterwards.
 *
 * Touch needs no separate path: a browser synthesises `mousedown` and `click` for a tap, so the
 * two above cover a finger as well as a mouse. It is the same reason `LobbyWalkControls` can
 * drive the walk from the mouse listeners alone on a desktop.
 */
function addListeners(navBar) {
    window.addEventListener('mousemove', event => hover(navBar, event), true);

    window.addEventListener('mousedown', event => swallow(navBar, event), true);
    window.addEventListener('mouseup', () => release(navBar), true);
    window.addEventListener('click', event => press(navBar, event), true);
}

/** What is under the pointer, and a cursor to match. Never stops the event. */
function hover(navBar, event) {
    const state = navBar.userData;
    const hit = pick(navBar, event);
    const next = hit === null ? null : (toggles(state, hit) ? state.logoGroup : hit);

    if (next !== state.hovered) {
        state.hovered = next;

        const canvas = document.getElementById('canvasEl');

        if (canvas) canvas.style.cursor = next ? 'pointer' : '';
    }
}

/** A press that begins on the HUD: the row's, not the canvas's — and the one that sinks a button. */
function swallow(navBar, event) {
    const hit = pick(navBar, event);

    if (!hit) return;

    event.stopPropagation();
    event.preventDefault();

    const state = navBar.userData;

    // Which button the pointer is down on, held until it comes up. The toggle is not a button and has
    // nothing to sink, so it leaves the row alone — its own press is the click, below.
    state.pressed = toggles(state, hit) ? null : hit;

    // What this press began on, kept for the click that may follow. `press` must act on this and not
    // on a second `pick` — the plate is about to move out from under the cursor, and why is there.
    state.armed = hit;
    state.armedX = event.clientX;
    state.armedY = event.clientY;
}

/**
 * The pointer came up, anywhere — which may well be nowhere near the button it went down on, so
 * this is on the window and not on the object. All it does is end the hold; a press that is also a
 * click is the click's business, and one that was a drag off the button is nobody's.
 */
function release(navBar) {
    navBar.userData.pressed = null;
}

/**
 * A press that ends on the HUD: the row's, and the one that acts.
 *
 * It acts on what the press **began** on rather than on what the ray finds now, and that is the
 * whole of the interesting part. A press moves the thing it lands on: `back` in `updateNavBar` sinks
 * a held plate along its own normal for as long as the button is down, and a plate on this row sits
 * high enough above the view axis that backing it up slides it *down the screen* by more than a
 * letter is tall. So by the time the click arrives the plate is no longer where the cursor is — and
 * a second `pick` at the same screen point loses exactly the small targets and keeps the large ones.
 * That is not a theory: it is a word carved in the middle of a plate going dead while the plate's own
 * body, which is three times the height, still covers the spot the cursor never left.
 *
 * So the answer is the one taken at `mousedown`, and the ray is asked only when there was no press to
 * begin with. The one exception is a press that has travelled: past `DRAG_SLOP` the pointer has left
 * the thing it grabbed, and the ray is asked again — which is also what makes a drag off a button
 * into empty space do nothing at all.
 */
function press(navBar, event) {
    const state = navBar.userData;

    const strayed = state.armed !== null &&
        Math.hypot(event.clientX - state.armedX, event.clientY - state.armedY) > DRAG_SLOP;

    const hit = state.armed !== null && !strayed ? state.armed : pick(navBar, event);

    // Spent either way, so a press with no click behind it — dragged out of the window and released
    // — cannot be answered by the next click that happens to arrive without one of its own.
    state.armed = null;

    if (!hit) return;

    event.stopPropagation();
    event.preventDefault();

    if (toggles(state, hit)) {
        // The coin is the toggle — and the head word with it, which labels it and, in a narrow
        // frame, heads the column: one press out, the next away.
        const wasOpen = state.open;

        state.open = !state.open;

        // Shutting the row is the Escape press. A visitor who puts the row away is done with this
        // part of the page, and the row is the only control that reaches every ring at once — so the
        // press means "out of here", and `goBack` is what "out of here" already means everywhere
        // else: a card first if one is out, then the planet the camera is on, and nothing at all
        // from home. Opening the row is not that press and does not call it, which is why the
        // toggle reads its own state before writing it rather than handing the decision over.
        if (wasOpen && state.onHome) state.onHome();

        return;
    }

    // The site's own two links, at the row's far end. A full-page move to the page the plate
    // names, and out of this app — nothing here answers for what is on the other side. The one
    // state change borrowed from the category press is the stacked column's closing: the choice
    // is made, whatever page the frame lands on next.
    const site = hit.userData.site;

    if (site) {
        if (state.stacked) state.open = false;

        window.location.assign(site.href);

        return;
    }

    // A button. `switchPlanet` decides the rest — from home this is a planet click with the ray
    // replaced by a word, and from a ring it is the straight move across.
    const planet = hit.userData.planet;

    if (!planet) return;

    // A category chosen from the column puts the column away. That form is drawn over the frame
    // rather than along its top, and the choice is made — the frame belongs to the flight now. A
    // row across a wide frame is in the way of nothing and is left as it was, which is also what
    // keeps a narrow frame's behaviour off the wide page. The head word stays out either way, so
    // the menu is one press from being back.
    if (state.stacked) state.open = false;

    if (state.onSelect) state.onSelect(planet);
}

/**
 * The first thing under the pointer that belongs to the HUD, or `null`.
 *
 * Only ever handed this file's own meshes and the coin, and the buttons only while the row is
 * out. That second condition is load-bearing: a raycaster reaches an object with `visible` false
 * exactly as it reaches one on screen, so a shut row would otherwise still be a set of invisible
 * targets lying across the top of the frame.
 *
 * **`#canvasEl` is a wrapper, not the renderer's canvas.** The id sits on the element the
 * renderer's own `<canvas>` is *inside* — the CSS calls it the containing block for the reticle —
 * so a move over the scene arrives with the inner `<canvas>` as its target and an identity test
 * against `#canvasEl` never passes. Containment is the question that was actually being asked:
 * did this land on the scene, or on the page beside it.
 *
 * Normalised against that wrapper's rect rather than read from `offsetX`/`offsetY` as
 * `GlobalFunctions` does, because this listener is on the window: `offsetX` is measured against
 * whatever element the event is passing through, and here that is not always the scene.
 */
function pick(navBar, event) {
    const state = navBar.userData;
    const canvas = document.getElementById('canvasEl');

    // The lobby gets a logo and nothing else. Asked here rather than in each of the three callers
    // because this is the one place that answers "is the HUD under the pointer", and all three
    // questions — does the cursor change, is the press swallowed, does the row open — are that
    // same question. A mark that is not hoverable cannot be pressed, and one that is not picked
    // cannot swallow the canvas press the lobby's door button needs.
    if (!state.isLive()) return null;

    if (!canvas || !canvas.contains(event.target)) return null;

    const rect = canvas.getBoundingClientRect();

    if (!rect.width || !rect.height) return null;

    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(pointer, getActiveCamera());

    // The admin door joins the targets only on the frames it shows (over Neptune). A raycaster
    // reaches an invisible object exactly as it reaches a visible one — the same trap the shut
    // row is gated against above — so a hidden door left in the ray would be a pressable nothing
    // in the middle of the frame everywhere else.
    const targets = (state.open ? state.targets : state.logoTarget).concat(
        state.door && state.door.visible ? [state.door] : [],
    );
    const hits = raycaster.intersectObjects(targets, true);

    // The head word is a target only on the frames it is out. A raycaster reaches an object with
    // `visible` false exactly as it reaches one on screen — the trap the shut row is gated against a
    // few lines above — and the word spends most of its life hidden, so left in it would make the
    // coin's label a hover target over a patch of empty frame beside it.
    //
    // The hit is resolved to the button or the toggle here rather than a few lines down, so that the
    // nearest *reachable* thing is the one answered with and not the nearest thing. Nothing overlaps
    // on the row today, so this is the same answer either way; it is written this way so that stays
    // true when something does.
    const hit = hits.find(candidate => candidate.object !== state.hint || state.hint.visible);

    if (!hit) return null;

    // A button is a group of three meshes now, and the ray lands on whichever of them is nearest
    // — a letter, or the plate around it. What all three mean is the button, so the hit is
    // resolved up to it here, once, and the three callers go on comparing whole buttons. The
    // coin is left as it was found: `owns` already answers for any of its parts.
    return buttonOf(state, hit.object) || hit.object;
}

/** The button `object` belongs to, or `null` if it belongs to none of them. The admin door is
 *  resolved like a button even though it is not in `buttons` — its press is a site move, and
 *  `pick`'s callers compare whole controls, not the mesh the ray happened to land on. */
function buttonOf(state, object) {
    for (let node = object; node; node = node.parent) {
        if (state.door && node === state.door) return node;
        if (state.buttons.indexOf(node) !== -1) return node;
    }

    return null;
}

/**
 * Whether a HUD hit is the row's toggle rather than one of its buttons: the coin, or the head word
 * that labels it.
 *
 * One question in one place, because three callers ask it and each asks something different of the
 * answer — `hover` reads it as "this is the coin", `swallow` as "nothing to sink", `press` as "open
 * or shut". Held apart, the three would have to be kept in step by hand, and the word was added to
 * two of them the day it became pressable.
 */
function toggles(state, object) {
    return owns(state.logoGroup, object) || object === state.hint;
}

/** Whether `object` is `group` or hangs off it. The coin is a group of four meshes. */
function owns(group, object) {
    for (let node = object; node; node = node.parent) {
        if (node === group) return true;
    }

    return false;
}
